import { Editor, TLShape } from "tldraw";

import {
  isLogicShape,
  LayoutBinding,
} from "@/components/canvas-editor/shapes/shapes";

/**
 * How many OTHER conditions share the `and` this node sits in.
 *
 * Only the nearest enclosing group counts, and only when it is an `and`. An
 * `or` is not a narrowing: every post reaching it is offered to every branch,
 * so a node inside one is a second way to match rather than a restriction on
 * the feed. `unshieldedMeteredNodes` in `src/lib/rate-limited-nodes.ts` draws
 * the same line for the same reason, as does `app/quota_shielding.py`.
 *
 * Returns 0 for a node whose parent is the feed itself: a filter that IS the
 * whole feed restricts nothing beyond itself, and saying otherwise would put a
 * warning on the 724 live feeds that are exactly one Users node.
 *
 * Placeholder bindings are the drop-target ghost the layout binding inserts
 * while a shape is being dragged, not a sibling condition, so they are not
 * counted -- otherwise the warning would flicker on during a drag.
 */
export function andSiblingCount(editor: Editor, shape: TLShape): number {
  const parentBinding = editor
    .getBindingsToShape<LayoutBinding>(shape, "layout")
    .find((binding) => !binding.props.placeholder);

  if (!parentBinding) return 0;

  const parent = editor.getShape(parentBinding.fromId);

  if (!parent || !isLogicShape(parent) || parent.props.type !== "and") {
    return 0;
  }

  const conjuncts = editor
    .getBindingsFromShape<LayoutBinding>(parent, "layout")
    .filter((binding) => !binding.props.placeholder);

  return Math.max(0, conjuncts.length - 1);
}
